import { InputLabel } from '@material-ui/core';
import Button from '@material-ui/core/Button';
import FormControl from '@material-ui/core/FormControl';
import FormHelperText from '@material-ui/core/FormHelperText';
import Icon from '@material-ui/core/Icon';
import Input from '@material-ui/core/Input';
import MenuItem from '@material-ui/core/MenuItem';
import Typography from '@material-ui/core/Typography';
import classNames from 'classnames';
import { isArray } from 'lodash';
import React, { CSSProperties, useEffect, useRef, useState } from 'react';
import Select, {
    components,
    IndicatorProps,
    InputActionMeta,
    MenuProps,
    OptionProps,
    PlaceholderProps,
    SelectComponentsConfig,
    SingleValueProps,
    StylesConfig,
    Theme,
    ValueContainerProps,
    ValueType,
    Props as SelectProps,
    ControlProps,
    MultiValueProps,
    MenuListComponentProps,
    InputProps,
    OptionTypeBase
} from 'react-select';
import AsyncSelect from 'react-select/async';
// NoticeProps Type isnt directly exported from react-select yet
import { NoticeProps } from 'react-select/src/components/Menu';
import { useAsync, UseAsyncStatus } from '../Hooks/useAsync';
import { CancelledRequest, HttpExecutorConfig, useHttpConfig } from '../Shared/HttpRequestExecutor';
import { Maybe, Nullable } from '../Shared/Types';
import { AutoCompleteTypes } from './MaskedTextField';
import { ReadOnlyField } from './ReadOnlyField';
import { StyledChip } from './StyledChip';
import styles from './StyledSelect.module.scss';

type SimpleModeConfig = {
    className: string;
};

export type SingleSelectProps<SuggestionType extends OptionTypeBase> = {
    label?: string;
    placeHolder?: string;
    readOnly?: boolean;
    helperText?: string;
    errorText?: string;
    warningText?: string;
    autoFocus?: boolean;
    required?: boolean;
    disabled?: boolean;
    maxResults: number;
    isMulti?: boolean;
    hideMultiChips?: boolean;
    allowMenuFloat?: boolean;
    noResultsMessage?: React.ReactNode;
    testId: string;
    simpleMode?: SimpleModeConfig;
    selectFirstAsyncResult?: boolean;
    hideWarningEmptyHelperText?: boolean;
    collapseTitleLabel?: boolean;
    noWrapValue?: boolean;
    optionHeight?: number;
    itemToString(suggestion: SuggestionType): string;
    itemToMenuKey(suggestion: SuggestionType): string;
    itemHasError?(suggestion: SuggestionType): boolean;
    getMultiValueChipIcon?(suggestion: SuggestionType): React.ReactElement;
    customItemRender?(suggestion: Nullable<SuggestionType>): React.ReactNode;
    selectedCustomItemRender?(suggestion: Nullable<SuggestionType>): React.ReactNode;
    suggestionCustomItemRender?(suggestion: Nullable<SuggestionType>): React.ReactNode;
    additionalActionButton?: {
        render(searchText: string, options: SuggestionType[]): React.ReactNode;
        onSelection(searchText: string): void;
        displayText(searchText: string): string;
    };
    onSelection?(item?: ValueType<SuggestionType | SuggestionType[]>): void;
    onOpen?(open: boolean): void;
    onChange?(value: string): void;
    onBlur?(e: React.FocusEvent): void;
    onClick?(e: React.MouseEvent): void;
    onClear?(): void;
};

export type AdditionalActionButtonType = {
    _isActionButton: boolean;
};

export type SuggestionTypes<T> = T | AdditionalActionButtonType;

function isAdditionalActionButtonType<SuggestionType>(
    option: ValueType<SuggestionTypes<SuggestionType>>
): option is AdditionalActionButtonType {
    if (option === null || option === undefined) return false;
    if (Array.isArray(option)) return false;
    if ('_isActionButton' in option) return true;
    return false;
}

function isValueTypeSuggestionType<SuggestionType>(
    option: ValueType<SuggestionTypes<SuggestionType>>
): option is ValueType<SuggestionType> {
    if (option === null || option === undefined) return true;
    if (Array.isArray(option)) return true;
    return !isAdditionalActionButtonType(option);
}

function NoOptionsMessage<SuggestionType>(props: NoticeProps<SuggestionType>) {
    if (props.selectProps.noResultsMessage)
        return React.cloneElement(props.selectProps.noResultsMessage, {
            ...props.innerProps
        });

    return (
        <Typography color='textSecondary' className={styles.noOptionsMessage} {...props.innerProps}>
            {props.children}
        </Typography>
    );
}

function inputComponent({ inputRef, ...props }: any) {
    return <div ref={inputRef} {...props} />;
}

function simpleInputComponent({ inputRef, ...props }: any) {
    return <div className={styles.simpleInput} ref={inputRef} {...props} />;
}

function Control<SuggestionType>(props: ControlProps<SuggestionType>) {
    let helperTextType: 'normal' | 'warning' | 'error' = 'normal';
    const unifiedHelperText: React.ReactNode =
        props.selectProps.errorText || props.selectProps.warningText || props.selectProps.helperText || '';

    if (props.selectProps.errorText) {
        helperTextType = 'error';
    } else if (props.selectProps.warningText) {
        helperTextType = 'warning';
    }

    const formControlClasses = classNames(styles.formControl, {
        [styles.errorState]: helperTextType === 'error'
    });

    const autoCompleteValue: string =
        props.selectProps.autoCompleteType === AutoCompleteTypes.UserInfo ? 'off-no-complete' : 'off';

    const labelClass = props.isDisabled ? styles.disabledInputLabel : styles.inputLabel;
    const labelClasses = {
        root: labelClass
    };

    const inputClasses = {
        root: styles.inputRoot,
        focused: styles.inputFocused,
        input: styles.input
    };

    const formHelperTextClasses = {
        root: classNames(styles.helperText, {
            [styles.warning]: helperTextType === 'warning'
        })
    };

    return (
        <FormControl
            error={!!props.selectProps.errorText}
            disabled={!!props.selectProps.disabled}
            fullWidth
            classes={{ root: formControlClasses }}
            data-test-id={`single-select-input_${props.selectProps.testId}`}
        >
            {!props.selectProps.collapseTitleLabel && (
                <InputLabel shrink classes={labelClasses}>
                    {props.selectProps.label || ''}
                    {props.selectProps.required && <span className={styles.requiredLabel}> *</span>}
                </InputLabel>
            )}
            <Input
                inputProps={{
                    className: styles.input,
                    inputRef: props.innerRef,
                    children: props.children,
                    disabled: props.selectProps.disabled,
                    readOnly: props.selectProps.readOnly,
                    'data-lpignore': 'true',
                    ...props.innerProps
                }}
                data-test-id={`single-select-menu-input_${props.selectProps.testId}`}
                autoComplete={autoCompleteValue}
                disabled={props.selectProps.disabled}
                readOnly={props.selectProps.readOnly}
                // Our placeholder gets restricted to string but gets encapsulated as react node from the select props
                placeholder={props.selectProps.placeholder as string}
                classes={inputClasses}
                inputComponent={inputComponent}
                className={styles.input}
                inputRef={props.innerRef}
            />
            {(!props.selectProps.hideWarningEmptyHelperText || unifiedHelperText) && (
                <FormHelperText classes={formHelperTextClasses}>{unifiedHelperText || ' '}</FormHelperText>
            )}
        </FormControl>
    );
}

function SimpleControl<SuggestionType>(props: ControlProps<SuggestionType>) {
    let helperTextType: 'normal' | 'warning' | 'error' = 'normal';
    const unifiedHelperText: React.ReactNode =
        props.selectProps.errorText || props.selectProps.warningText || props.selectProps.helperText;
    const inputClasses = {
        root: styles.inputRoot,
        input: styles.input
    };

    if (props.selectProps.errorText) {
        helperTextType = 'error';
    } else if (props.selectProps.warningText) {
        helperTextType = 'warning';
    }

    const formHelperTextClasses = {
        root: classNames(styles.helperText, {
            [styles.warning]: helperTextType === 'warning',
            [styles.error]: helperTextType === 'error'
        })
    };

    const autoCompleteValue: string =
        props.selectProps.autoCompleteType === AutoCompleteTypes.UserInfo ? 'off-no-complete' : 'off';
    const simpleModeConfig = props.selectProps.simpleMode as SimpleModeConfig;

    return (
        <FormControl
            error={!!props.selectProps.errorText}
            disabled={!!props.selectProps.disabled}
            fullWidth
            data-test-id={`single-select-input_${props.selectProps.testId}`}
        >
            <Input
                inputProps={{
                    className: classNames(styles.input, simpleModeConfig.className),
                    inputRef: props.innerRef,
                    children: props.children,
                    readOnly: props.selectProps.readOnly,
                    ...props.innerProps
                }}
                autoComplete={autoCompleteValue}
                readOnly={props.selectProps.readOnly}
                // Our placeholder gets restricted to string but gets encapsulated as react node from the select props
                placeholder={props.selectProps.placeholder as string}
                classes={inputClasses}
                inputComponent={simpleInputComponent}
                className={styles.inputSimple}
                disableUnderline
            />
            {(!props.selectProps.hideWarningEmptyHelperText || unifiedHelperText) && (
                <FormHelperText classes={formHelperTextClasses}>{unifiedHelperText || ' '}</FormHelperText>
            )}
        </FormControl>
    );
}

function Option<SuggestionType>(props: OptionProps<SuggestionType>) {
    let contents: React.ReactNode = props.children;

    const index = props.options.findIndex(
        it => props.selectProps.itemToMenuKey(props.data) === props.selectProps.itemToMenuKey(it)
    );

    const dataTestId = `single-select-menu-item-${index}`;

    if (props.selectProps.additionalActionButton && props.data?._isActionButton) {
        const actionButtonRenderValue = props.selectProps.additionalActionButton.render(
            props.selectProps.asyncInputValue ?? props.selectProps.inputValue,
            props.options ? props.options.filter(x => !isAdditionalActionButtonType(x)) : []
        );
        if (actionButtonRenderValue === null || actionButtonRenderValue === undefined) return null;
        contents = React.cloneElement(actionButtonRenderValue, {
            'data-test-id': dataTestId
        });
    } else {
        if (props.selectProps.suggestionCustomItemRender) {
            const suggestionCustomItemRenderValue = props.selectProps.suggestionCustomItemRender(props.data);
            if (suggestionCustomItemRenderValue === null || suggestionCustomItemRenderValue === undefined) return null;
            contents = React.cloneElement(suggestionCustomItemRenderValue, {
                'data-test-id': dataTestId
            });
        } else if (props.selectProps.customItemRender) {
            const customItemRenderValue = props.selectProps.customItemRender(props.data);
            if (customItemRenderValue === null || customItemRenderValue === undefined) return null;
            contents = React.cloneElement(customItemRenderValue, {
                'data-test-id': dataTestId
            });
        }
    }

    return (
        <MenuItem
            buttonRef={props.innerRef}
            selected={props.isFocused}
            component='div'
            style={{
                fontWeight: props.isSelected ? 600 : 400,
                minHeight: 24,
                maxHeight: 65,
                height: 'auto',
                display: 'block',
                textOverflow: 'ellipsis',
                whiteSpace: props.selectProps.wrapOptions ? 'normal' : 'nowrap'
            }}
            data-test-id={dataTestId}
            {...props.innerProps}
        >
            {contents}
        </MenuItem>
    );
}

function Placeholder<SuggestionType>(props: PlaceholderProps<SuggestionType>) {
    return (
        <Typography color='textSecondary' className={styles.placeholder} {...props.innerProps}>
            {props.selectProps.placeHolder}
        </Typography>
    );
}

function SingleValue<SuggestionType>(props: SingleValueProps<SuggestionType>) {
    if (props.selectProps.selectedCustomItemRender) {
        const selectedItemCustomRenderValue = props.selectProps.selectedCustomItemRender(props.data);
        if (selectedItemCustomRenderValue === null || selectedItemCustomRenderValue === undefined) return null;
        return React.cloneElement(selectedItemCustomRenderValue, { ...props.innerProps });
    } else if (props.selectProps.customItemRender) {
        const customItemRenderValue = props.selectProps.customItemRender(props.data);
        if (customItemRenderValue === null || customItemRenderValue === undefined) return null;
        return React.cloneElement(customItemRenderValue, { ...props.innerProps });
    }

    return (
        <Typography className={styles.singleValue} {...props.innerProps}>
            {props.children}
        </Typography>
    );
}

function ValueContainer<SuggestionType>(props: ValueContainerProps<SuggestionType>) {
    const classes = classNames(styles.valueContainer, {
        [styles.fixedHeight]: props.selectProps?.simpleMode,
        [styles.notSearchable]: props.selectProps.isSearchable !== undefined && !props.selectProps.isSearchable,
        [styles.noFlexWrap]: props.selectProps.noWrapValue
    });
    return <div className={classes}>{props.children}</div>;
}

function MultiValue<SuggestionType>(props: MultiValueProps<SuggestionType>) {
    const icon: JSX.Element = props.selectProps.getMultiValueChipIcon
        ? props.selectProps.getMultiValueChipIcon(props.data)
        : undefined;

    if (props.selectProps.hideMultiChips) {
        return null;
    }

    return (
        <StyledChip
            avatar={icon}
            tabIndex={-1}
            // Type here is wrong children is always a string
            label={props.children as string}
            hasAccent
            maxLength={25}
            focused={props.isFocused}
            onRemove={props.removeProps.onClick}
            hasError={props.selectProps?.itemHasError ? props.selectProps.itemHasError(props.data) : false}
            testId={`${props.selectProps.testId}-${props.selectProps.itemToMenuKey(props.data)}`}
        />
    );
}

function MenuList<SuggestionType>(props: MenuListComponentProps<SuggestionType>) {
    return (
        <div className={styles.menuList} data-test-id={'single-select-menu'}>
            {props.children}
        </div>
    );
}

function Menu<SuggestionType>(props: MenuProps<SuggestionType>) {
    return (
        <div
            className={classNames({
                [styles.hasMoreContainer]: props.selectProps.hasMoreResults
            })}
        >
            <components.Menu {...props}>
                <div className={styles.dropdownBody}>
                    {props.children}
                    {props.selectProps.hasMoreResults && (
                        <Typography className={styles.maxResults}>More results found - type to refine</Typography>
                    )}
                </div>
            </components.Menu>
        </div>
    );
}

function InputComponent(props: InputProps) {
    return <components.Input {...props} className={styles.forceInputFontFamily} />;
}

function ClearIndicator<SuggestionType>(props: IndicatorProps<SuggestionType>) {
    const buttonClasses = {
        root: classNames(styles.clearButton, {
            [styles.clearButtonFocused]: props.isFocused
        })
    };

    return (
        <Button
            {...props.innerProps}
            size='small'
            classes={buttonClasses}
            data-test-id={`single-select-clear_${props.selectProps.testId}`}
            tabIndex={-1}
        >
            <Icon classes={{ root: styles.iconRoot }}>cancel</Icon>
        </Button>
    );
}

function DropdownIndicator<SuggestionType>(props: IndicatorProps<SuggestionType>) {
    return (
        <div
            {...props.innerProps}
            className={styles.arrowIcon}
            data-test-id={`single-select-drop-down_${props.selectProps.testId}`}
        >
            {props.selectProps.menuIsOpen ? <Icon>arrow_drop_up</Icon> : <Icon>arrow_drop_down</Icon>}
        </div>
    );
}

const loadingIndicator = (css: CSSProperties) => {
    css.position = 'absolute';
    css.right = 31;
    css.backgroundColor = 'transparent';

    return css;
};

const styleOverrides: StylesConfig = {
    loadingIndicator,
    menuPortal: base => ({ ...base, width: 'auto', minWidth: base.width + 'px', maxWidth: '600px', zIndex: 9999 })
};

const themeOverride = (theme: Theme): Theme => ({
    ...theme,
    borderRadius: 0,
    spacing: { ...theme.spacing, menuGutter: 1 }
});

function alwaysTrueFilterOptions() {
    return true;
}

export function isSingleSelectSingleValue<T>(item?: ValueType<T | T[]>): item is T {
    if (item) {
        if ((item as T[]).length === undefined && !isArray(item)) return true;
    }

    return false;
}

// From react-select docs
// minMenuHeight: Minimum height of the menu before flipping
// Default option height is 42 (26 + 2x8 padding).
// If you pass in optionHeight prop make sure it's the total height of options.
// More results message height is 40.
function calcMinMenuHeight<SuggestionType>(props: SingleSelectProps<SuggestionType>, moreResults: boolean, resultLength: number): number {
    const optionHeight = props.optionHeight ?? 42;
    let maxResults = props.maxResults > 0 ? props.maxResults : resultLength;
    if (props.additionalActionButton) maxResults++;
    let minHeight = optionHeight * maxResults;
    if (moreResults) minHeight += 40;
    return minHeight;
}

export function StyledSelect<SuggestionType>(
    props: SingleSelectProps<SuggestionType> & {
        value?: Nullable<SuggestionType> | SuggestionType[];
        useCache?: boolean;
        defaultValue?: Nullable<SuggestionType> | SuggestionType[];
        suggestions?: SuggestionType[];
        autoCompleteType?: AutoCompleteTypes;
        autoFocus?: boolean;
        isClearable: boolean;
        isSearchable?: boolean;
        wrapOptions?: boolean;
        noWrapReadOnly?: boolean;
        selectRef?(inputElement: Nullable<AsyncSelect<SuggestionType> | Select<SuggestionType>>): void;
        fetch?(
            config: HttpExecutorConfig,
            inputValue: string,
            limit: number
        ): Promise<SuggestionType[] | CancelledRequest>;
        onFocus?(): void;
        onClick?(): void;
        hideOptionsWhenReloading?: boolean;
    }
): JSX.Element {
    const httpConfig = useHttpConfig();

    function getItemDisplayValue(value: SuggestionType): React.ReactNode {
        const icon: Maybe<React.ReactElement> = props.getMultiValueChipIcon
            ? props.getMultiValueChipIcon(value)
            : undefined;

        return props.customItemRender ? (
            props.customItemRender(value)
        ) : props.isMulti ? (
            <StyledChip
                avatar={icon}
                key={props.itemToMenuKey(value)}
                tabIndex={-1}
                label={props.itemToString(value)}
                hasAccent
                maxLength={25}
                readOnly
                testId={`chip-${props.itemToString(value)}`}
            />
        ) : (
            props.itemToString(value)
        );
    }
    const [menuOpen, setMenuOpen] = useState(false);
    const firstLoadRef = useRef(true);
    const firstLoad = firstLoadRef.current;
    const searchText = useRef('');

    const itemToString = (item: SuggestionTypes<SuggestionType>) =>
        isValueTypeSuggestionType(item)
            ? props.itemToString(item)
            : props.additionalActionButton
            ? props.additionalActionButton.displayText(searchText.current)
            : 'itemToString:AdditionalActionButton';
    const itemToKey = props.itemToMenuKey
        ? (item: SuggestionTypes<SuggestionType>) =>
              isValueTypeSuggestionType(item) ? props.itemToMenuKey(item) : 'itemToKey:AdditionalActionButton'
        : itemToString;

    const replacementComponents: SelectComponentsConfig<SuggestionTypes<SuggestionType>> = {
        Control: props.simpleMode ? SimpleControl : Control,
        Menu,
        MultiValue,
        NoOptionsMessage,
        Option,
        MenuList,
        Placeholder,
        SingleValue,
        ValueContainer,
        ClearIndicator,
        Input: InputComponent,
        DropdownIndicator: props.simpleMode ? undefined : DropdownIndicator
    };

    if (!props.fetch && !props.suggestions) console.error('Single Select has been given no fetch or suggestions');

    const errorAPICallback = () => Promise.reject('Error, no fetch function given to ASYNC select');

    const asyncFunction: (
        config: HttpExecutorConfig,
        inputValue: string,
        limit: number
    ) => Promise<SuggestionType[] | CancelledRequest> = props.fetch
        ? async (config, inputValue, limit) => {
              let result: SuggestionType[] | CancelledRequest = [];
              if (props.fetch) {
                  result = await props.fetch(config, inputValue, limit);
                  searchText.current = inputValue;
              }
              return result;
          }
        : errorAPICallback;

    const queryAmount = props.maxResults > 0 ? props.maxResults + 1 : 0;
    const asyncFetchState = useAsync<SuggestionType[]>({
        defaultValue: [],
        httpConfig,
        useCache: props.useCache === undefined ? true : props.useCache,
        getApi: asyncFunction
    });

    useEffect(() => {
        async function fetchFirstResult() {
            if (props.selectFirstAsyncResult && props.fetch && props.onSelection) {
                const results = await props.fetch(httpConfig, '', 1);
                if (results instanceof CancelledRequest) return;

                firstLoadRef.current = false;
                if (results.length > 0) props.onSelection(results[0]);
            }
        }

        if (firstLoad && !props.value && props.fetch && props.selectFirstAsyncResult) {
            fetchFirstResult();
        }
    }, []);

    function onSelection(value: ValueType<SuggestionTypes<SuggestionType>>) {
        if (!props.onSelection) return;
        if (isValueTypeSuggestionType(value)) props.onSelection(value);
        if (isAdditionalActionButtonType(value) && props.additionalActionButton)
            props.additionalActionButton.onSelection(searchText.current);
    }

    if (props.readOnly) {
        const output = !props.value
            ? ''
            : !Array.isArray(props.value)
            ? getItemDisplayValue(props.value)
            : props.value.map(getItemDisplayValue);

        return (
            <ReadOnlyField
                label={props.label || null}
                mode='field'
                noWrapping={props.noWrapReadOnly}
                hideHelperText={props.hideWarningEmptyHelperText}
            >
                {output}
            </ReadOnlyField>
        );
    }

    const selectProps: SelectProps<SuggestionTypes<SuggestionType>> = {
        ...props,
        ref: props.selectRef,
        styles: props.simpleMode ? undefined : styleOverrides,
        components: replacementComponents,
        getOptionLabel: itemToString,
        getOptionValue: itemToKey,
        value: props.value,
        isMulti: props.isMulti,
        isClearable: props.isClearable,
        hideSelectedOptions: false,
        onChange: onSelection,
        placeholder: props.placeHolder,
        isDisabled: props.disabled,
        theme: themeOverride,
        minMenuHeight: 241, // force a flip if less space than this available
        wrapOptions: props.wrapOptions
    };

    if (props.fetch) {
        let results: SuggestionTypes<SuggestionType>[] = asyncFetchState.data || [];

        let hasMoreResults = false;
        if (props.maxResults > 0) {
            hasMoreResults = results.length > props.maxResults && asyncFetchState.status === UseAsyncStatus.Loaded;
            if (results.length > props.maxResults) {
                results = results.filter((x, i) => i < props.maxResults);
            }
        }

        if (props.additionalActionButton) {
            results = [...results, { _isActionButton: true }];
        }

        const handleInputChange = (value: string, reason: InputActionMeta) => {
            if (reason.action === 'input-change') {
                asyncFetchState.debouncedGetApi(value, queryAmount);
            }
        };

        const handleMenuOpen = () => {
            if (!menuOpen) {
                setMenuOpen(true);
                props.onOpen && props.onOpen(true);
                asyncFetchState.getApi('', queryAmount);
            }
        };

        const handleMenuClose = () => {
            setMenuOpen(false);
            props.onOpen && props.onOpen(false);
        };

        return (
            <Select<SuggestionTypes<SuggestionType>>
                {...selectProps}
                filterOption={alwaysTrueFilterOptions}
                menuPortalTarget={document.getElementById('menu-portal')}
                hasMoreResults={hasMoreResults}
                menuPosition='fixed'
                menuPlacement='auto'
                onInputChange={handleInputChange}
                // don't open until loading finishes.  stay open for reloads
                menuIsOpen={
                    menuOpen &&
                    asyncFetchState.status !== UseAsyncStatus.Loading &&
                    (asyncFetchState.status !== UseAsyncStatus.Reloading || !props.hideOptionsWhenReloading)
                }
                onMenuOpen={handleMenuOpen}
                onMenuClose={handleMenuClose}
                isLoading={
                    asyncFetchState.status === UseAsyncStatus.Reloading ||
                    asyncFetchState.status === UseAsyncStatus.Loading
                }
                options={results}
                asyncInputValue={searchText.current}
                minMenuHeight={calcMinMenuHeight(props, hasMoreResults, results.length)}
            />
        );
    }

    // Non-async render
    let suggestions: SuggestionTypes<SuggestionType>[] = props.suggestions || [];
    if (props.additionalActionButton) {
        suggestions = [...suggestions, { _isActionButton: true }];
    }

    const handleInputChange = (value: string, reason: InputActionMeta) => {
        if (reason.action === 'input-change') {
            searchText.current = value;
        }
    };

    return (
        <Select<SuggestionTypes<SuggestionType>>
            {...selectProps}
            isSearchable={props.isSearchable}
            menuPortalTarget={document.getElementById('menu-portal')}
            menuPosition='fixed'
            menuPlacement='auto'
            options={suggestions}
            onInputChange={handleInputChange}
        />
    );
}
